
import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { QuickDeleteProvider } from "./contexts/QuickDeleteContext";
import { UploadProgressProvider, useUploadProgress } from "./contexts/UploadProgressContext";
import { UploadProgressToast } from "./components/UploadProgressToast";
import { ExportProgressProvider } from "./contexts/ExportProgressContext";
import { ExportProgressToast } from "./components/ExportProgressToast";
import { SentryErrorBoundary, SentryRouteTracker } from "./components/ErrorBoundary";
import AuthPage from "./components/AuthPage";
import ResetPassword from "./pages/ResetPassword";
import { DataUsageMonitor } from "./components/admin/DataUsageMonitor";
import { PlainPageFallback, RouteSkeleton } from "./components/LoadingSkeletons";
import { hasPersistedSession } from "./lib/layoutMemory";

// Route-level code splitting.
//
// Everything used to land in one ~2.3MB chunk, so a first-time visitor
// downloaded the editor, the admin dashboard and the sandbox before the
// projects list could paint.
//
// Eager (in the main chunk): AuthPage and ResetPassword, because they are the
// first thing a logged-out visitor sees and a spinner there would be a
// regression; and the providers/toasts below, which mount on every route.
//
// Lazy: Projects and Project are the two big authenticated screens — an
// authenticated user needs one or the other, never both up front. AdminDashboard
// pulls in recharts (~5MB in node_modules) and is reachable by a handful of
// people. Sandbox, AccountCreated and NotFound are rarely hit at all.
const Projects = React.lazy(() => import("./pages/Projects"));
const Project = React.lazy(() => import("./pages/Project"));
const AccountCreated = React.lazy(() => import("./pages/AccountCreated"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const Sandbox = React.lazy(() => import("./pages/Sandbox"));
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));
const JoinProject = React.lazy(() => import("./pages/JoinProject"));

/**
 * Fallback while a route chunk loads.
 *
 * A cold load used to step through three layouts — centred "Loading…" for the
 * chunk, then the page's own skeleton, then the page — so the content moved
 * twice before settling. Handing the chunk fallback the *same* skeleton the
 * page will show collapses that to one transition.
 *
 * The logged-out tree keeps `PlainPageFallback`: its routes are the auth
 * screens, where a projects skeleton would be a lie.
 */
const RouteFallback: React.FC = () => {
  // Inside the router, so the pathname is the one being navigated to — the
  // fallback has to know which page's skeleton to draw.
  const { pathname } = useLocation();
  return <RouteSkeleton pathname={pathname} />;
};

const queryClient = new QueryClient();

const AppContent: React.FC = () => {
  const { user, loading, isPasswordRecovery, clearPasswordRecovery } = useAuth();

  // Auth hasn't resolved yet, so we don't know whether this visitor gets the
  // app or the login screen. If a session is already on disk they're almost
  // certainly getting the app, and showing the destination page's skeleton now
  // means the layout is settled before the first byte of data arrives. With no
  // stored session, stay neutral — a projects skeleton followed by a login form
  // would be a bigger jump than the spinner it replaced.
  if (loading) {
    return hasPersistedSession()
      ? <RouteSkeleton pathname={window.location.pathname} />
      : <PlainPageFallback />;
  }

  // Password recovery outranks both route trees. Supabase has already turned the
  // emailed link into a real session, so routing normally here would drop the
  // user straight into the app without ever asking for a new password (#8).
  // Rendering outside the router also means no path can navigate away from it.
  if (isPasswordRecovery) {
    return <ResetPassword onComplete={clearPasswordRecovery} />;
  }

  if (!user) {
    return (
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <SentryRouteTracker />
        <React.Suspense fallback={<PlainPageFallback />}>
          <Routes>
            <Route path="/account-created" element={<AccountCreated />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<AuthPage />} />
          </Routes>
        </React.Suspense>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <SentryRouteTracker />
      <React.Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Projects />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/project/:projectId" element={<Project />} />
          <Route path="/join/:token" element={<JoinProject />} />
          <Route path="/sandbox" element={<Sandbox />} />
          <Route path="/reset-password" element={<ResetPassword onComplete={clearPasswordRecovery} />} />
          <Route path="/system_admin/dashboard" element={<AdminDashboard />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </React.Suspense>
      <DataUsageMonitor />
    </BrowserRouter>
  );
};

const ToasterWithUploadProgress: React.FC = () => {
  const uploadProgress = useUploadProgress();

  return (
    <Toaster
      uploadProgressSlot={
        <div className="flex flex-col gap-2 items-end">
          <ExportProgressToast />
          <UploadProgressToast
            isUploading={uploadProgress.isUploading}
            totalCount={uploadProgress.totalCount}
            completedCount={uploadProgress.completedCount}
            failedCount={uploadProgress.failedCount}
            mediaType={uploadProgress.mediaType}
            individualProgress={uploadProgress.individualProgress}
            onDismiss={uploadProgress.resetUpload}
          />
        </div>
      }
    />
  );
};

const App: React.FC = () => {
  return (
    <SentryErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <UploadProgressProvider>
            <ExportProgressProvider>
              <QuickDeleteProvider>
                <TooltipProvider>
                  <ToasterWithUploadProgress />
                  <Sonner />
                  <AppContent />
                </TooltipProvider>
              </QuickDeleteProvider>
            </ExportProgressProvider>
          </UploadProgressProvider>
        </AuthProvider>
      </QueryClientProvider>
    </SentryErrorBoundary>
  );
};

export default App;
